import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, CommonSectorService, SectorFilter, Sector, UUID, CommonGlobalService, CallbackErrorAxios, CallbackWarningAxios, Client } from '@umanforge/common';
import { ModifySector, PostSector } from 'interfaces/api_sector.interfaces';
import { ImportError } from 'interfaces/api_import.interfaces';
import { isHeaderProblem } from 'utils/import.utils';


export class SectorService {

  static getSector(
    sectorId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Sector> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSectorService.getSector( sectorId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedSectors(
    filters: SectorFilter | null,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Sector[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSectorService.getAllowedSectors( filters, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedSectorsBySitesId(
    sitesId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Sector[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSectorService.getAllowedSectorsBySitesId( sitesId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static postSector(
    body: PostSector,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( async resolve => {
      AXIOS.post( API.SECTORS, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 ? response.data.id : null );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

  static deleteSector(
    sectorId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SECTORS + sectorId, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static putSector(
    sectorId: UUID, body: ModifySector,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.SECTORS + sectorId, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static postAdminsToSector(
    sectorId: UUID, body: { admins: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.SECTORS + sectorId + '/admins', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteAdminsToSector(
    sectorId: UUID, body: { admins: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SECTORS + sectorId + '/admins', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postUsersToSector(
    sectorId: UUID, body: { users: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.SECTORS + sectorId + '/users', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteUsersToSector(
    sectorId: UUID, body: { users: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SECTORS + sectorId + '/users', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static exportSectorsOfClient(
    client: Client,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios,
    filters?: { filterSites?: UUID[] }
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SECTORS }export/${ client.id }`, {
        ...await CommonGlobalService.getHeaderRequest( token ),
        params: filters,
        responseType: 'blob'
      } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ client.name }_sectors_export.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static downloadSectorsTemplate(
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SECTORS }export`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'sectors_template.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static importSectorsOnClient(
    clientId: UUID, file: File, hasHeader: boolean | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID[] | ImportError | null> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( hasHeader !== undefined ) {
        formData.append( 'hasHeader', String( hasHeader ) );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.SECTORS }import/${ clientId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          let error: ImportError | null = null;
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && !( isHeaderProblem( response.data ) && hasHeader === undefined ) ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 202 ) {
            error = response.data as ImportError;
          }

          resolve( response.status === 200 ? response.data.ids : error );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

};